import { useState, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';
import { fetchToken, fetchUsers, handleChangeUserRole, handleSuspendUser, handleUpdateUserDetails, handleDeleteUser } from '../ApiHandler/usersFunctions';
import { fetchStates } from '../ApiHandler/schoolFunctions';
import { fetchExistingLabTypes } from '../ApiHandler/labFunctions';
import { exportToUserCSV, exportToUserExcel, exportToPDF, handlePrint } from '../../utils/Utils';
import { handleCreateUser } from '../ApiHandler/authFunctions';
import usePagination from '../../hooks/usePagination';
import ConfirmationModal from '../../utils/ConfirmationModal';

// ── Role badge ────────────────────────────────────────────────
const ROLE_META = {
    1: { label:'Admin',         icon:'bx-crown', bg:'#fff3cd', color:'#856404' },
    2: { label:'Mentor',        icon:'bx-user',  bg:'#d1ecf1', color:'#0c5460' },
    3: { label:'State Officer', icon:'bx-map',   bg:'#d4edda', color:'#155724' },
};
const RoleBadge = ({ roleId }) => {
    const r = ROLE_META[roleId] || { label:'Unknown', icon:'bx-help-circle', bg:'#f8d7da', color:'#721c24' };
    return (
        <span style={{ display:'inline-flex', alignItems:'center', gap:5,
            padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:700,
            background:r.bg, color:r.color }}>
            <i className={`bx ${r.icon}`} />{r.label}
        </span>
    );
};

const inp = {
    width:'100%', padding:'9px 12px', border:'1px solid #dee2e6',
    borderRadius:6, fontSize:13, outline:'none', boxSizing:'border-box', marginBottom:2
};

const UserAccess = () => {
    const [users,         setUsers]         = useState([]);
    const [selectedUser,  setSelectedUser]  = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [searchQuery,   setSearchQuery]   = useState('');
    const [isPopupOpen,   setIsPopupOpen]   = useState(false);
    const [editPopupOpen, setEditPopupOpen] = useState(false);
    const [isModalOpen,   setIsModalOpen]   = useState(false);
    const [modalAction,   setModalAction]   = useState(null);
    const [loading,       setLoading]       = useState(false);
    const [states,        setStates]        = useState([]);
    const [availableLabs, setAvailableLabs] = useState([]);
    const [newUser,       setNewUser]       = useState({
        fname:'', lname:'', email:'', phone:'', userType:'', state:'', assignedLab:''
    });

    useEffect(() => {
        fetchToken(setCurrentUserId);
        fetchUsers(setUsers);
        fetchStates(setStates);
        fetchExistingLabTypes(setAvailableLabs);
    }, []);

    const handleInputChange  = e => { const{name,value}=e.target; setNewUser(p=>({...p,[name]:value})); };
    const handleUpdateChange = e => { const{name,value}=e.target; setSelectedUser(p=>({...p,[name]:value})); };

    const confirmHandleDeleteUser = (userId, userEmail) => {
        setModalAction(() => () => handleDeleteUser(userId, userEmail, currentUserId, setUsers, users));
        setIsModalOpen(true);
    };
    const handleConfirmAction = () => { setIsModalOpen(false); if (modalAction) modalAction(); };

    // ── FIX: pre-select dropdown using assignedLabTypeId (not assigned_lab_id) ──
    const handleEditUserDetails = (user) => {
        const parts = (user.username || '').split(' ');
        setSelectedUser({
            ...user,
            fname: parts[0] || '',
            lname: parts.slice(1).join(' ') || '',
            assignedLab: user.assignedLabTypeId || '' // ← FIXED
        });
        setEditPopupOpen(true);
    };

    const filteredUsers = users.filter(u =>
        u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const {
        currentPage, entriesPerPage, currentEntries,
        handlePageChange, handleEntriesChange,
        totalEntries, startEntry, endEntry, totalPages
    } = usePagination(filteredUsers, 10);

    return (
        <div className="artifacts-container">
            <ToastContainer />
            <ConfirmationModal
                isOpen={isModalOpen}
                title="Confirm Delete User"
                message="Are you sure you want to delete this user? This action cannot be undone."
                onConfirm={handleConfirmAction}
                onCancel={() => setIsModalOpen(false)}
            />

            {/* HEADER */}
            <header className="artifacts-header"
                style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <h1>User Access</h1>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    {loading && <Oval height="20" width="20" color="blue" ariaLabel="loading" />}
                    <button type="button"
                        style={{ backgroundColor:'#007BFF', padding:'8px 16px', color:'white',
                            border:'none', borderRadius:4, cursor:'pointer', fontWeight:600 }}
                        onClick={() => setIsPopupOpen(true)}>
                        Create User
                    </button>
                </div>
            </header>

            <div className="artifacts-table-container">
                {/* TOP BAR */}
                <div className="header-select-entries">
                    <th className="select-entries">Show
                        <select onChange={handleEntriesChange} value={entriesPerPage}>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>entries
                    </th>
                    <th>
                        <div className="table-buttons">
                            <button onClick={() => exportToUserCSV(filteredUsers, 'DMS Users.csv')}>CSV</button>
                            <button onClick={() => exportToUserExcel(filteredUsers, 'DMS Users.xlsx')}>Excel</button>
                            <button onClick={() => exportToPDF('.artifacts-table', 'DMS Users.pdf')}>PDF</button>
                            <button onClick={() => handlePrint('.artifacts-table-container')}>Print</button>
                        </div>
                    </th>
                    <th className="user-search">
                        <label>Search</label>
                        <input type="text" placeholder="Type Name or Email..."
                            className="user-search-bar" value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)} />
                    </th>
                </div>

                {/* TABLE */}
                <div className="artifacts-table-view">
                    <table className="artifacts-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Username</th>
                                <th>Email</th>
                                <th>State & Lab Type</th>
                                <th>Current Role</th>
                                <th>Delete User</th>
                                <th>Change User Role</th>
                                <th>Suspend User</th>
                                <th>Edit Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentEntries.map((user, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td style={{ fontWeight:600 }}>{user.username}</td>
                                    <td style={{ fontSize:13, color:'#555' }}>{user.email}</td>

                                    {/* ── State & Lab Type — FIX: show assignedLabTypeName ── */}
                                    <td>
                                        {user.state || user.assignedLabTypeName ? (
                                            <div style={{ display:'flex', flexWrap:'wrap', gap:4, alignItems:'center' }}>
                                                {user.state && (
                                                    <span style={{ display:'inline-flex', alignItems:'center', gap:3,
                                                        background:'#e8f4fd', color:'#1565c0',
                                                        borderRadius:12, padding:'2px 8px',
                                                        fontSize:11, fontWeight:600, whiteSpace:'nowrap' }}>
                                                        <i className="bx bx-map-pin" style={{ fontSize:11 }} />
                                                        {user.state}
                                                    </span>
                                                )}
                                                {/* ── FIX: use assignedLabTypeName (e.g. SATHEE, Atal) ── */}
                                                {user.assignedLabTypeName && (
                                                    <span style={{ display:'inline-flex', alignItems:'center', gap:3,
                                                        background:'#e8f5e9', color:'#2e7d32',
                                                        borderRadius:12, padding:'2px 8px',
                                                        fontSize:11, fontWeight:600, whiteSpace:'nowrap' }}>
                                                        <i className="bx bx-test-tube" style={{ fontSize:11 }} />
                                                        {user.assignedLabTypeName}
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <span style={{ color:'#bbb', fontSize:12 }}>—</span>
                                        )}
                                    </td>

                                    <td><RoleBadge roleId={user.role_id} /></td>

                                    <td>
                                        <button className="delete-btn"
                                            onClick={() => confirmHandleDeleteUser(user.id, user.email)}
                                            disabled={user.id === currentUserId}
                                            style={{ opacity:user.id===currentUserId ? .5 : 1,
                                                cursor:user.id===currentUserId?'not-allowed':'pointer' }}>
                                            <i className="bx bx-trash" /> Delete
                                        </button>
                                    </td>

                                    <td>
                                        <select className="role-select-dropdown" value={user.role_id}
                                            onChange={e => handleChangeUserRole(user.id, user.email,
                                                parseInt(e.target.value), currentUserId, setUsers, users)}>
                                            <option value={1}>Admin</option>
                                            <option value={2}>Mentor</option>
                                            <option value={3}>State Officer</option>
                                        </select>
                                    </td>

                                    <td>
                                        <label className="switch">
                                            <div className="in-row-input">
                                                <input type="checkbox"
                                                    checked={user.status === 'inactive'}
                                                    disabled={loading}
                                                    onChange={e => handleSuspendUser(e, user.id,
                                                        currentUserId, user.username, user.email,
                                                        user.status, setUsers, setLoading)} />
                                                <span className="slider round" />
                                            </div>
                                        </label>
                                    </td>

                                    <td>
                                        <a href="# " className="edit-link"
                                            onClick={() => handleEditUserDetails(user)}>
                                            ✏️ Edit
                                        </a>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                <div className="pagination">
                    <p>Showing {startEntry} to {endEntry} of {totalEntries} entries</p>
                    <div className="pagination-buttons">
                        <button onClick={() => handlePageChange(currentPage-1)} disabled={currentPage===1}>Previous</button>
                        {Array.from({ length:totalPages }, (_,i) => (
                            <button key={i+1} className={currentPage===i+1?'active':''}
                                onClick={() => handlePageChange(i+1)}>{i+1}</button>
                        ))}
                        <button onClick={() => handlePageChange(currentPage+1)} disabled={currentPage===totalPages}>Next</button>
                    </div>
                </div>
            </div>

            {/* USAGE INSTRUCTIONS */}
            <div className="usage-instructions">
                <h2>📢 Usage Instructions</h2>
                <ul>
                    <li><i className="bx bx-paper-plane" /> User role can be changed.</li>
                    <li><i className="bx bx-paper-plane" /> The logged-in user can't suspend or change their own role.</li>
                    <li><i className="bx bx-paper-plane" /> All activities done here will be logged.</li>
                    <li><i className="bx bx-paper-plane" /> Suspended users will not be able to log in.</li>
                    <li><i className="bx bx-paper-plane" /> State Officers and Mentors must be assigned to a specific state and lab type.</li>
                    <li><i className="bx bx-paper-plane" /> Users will only see data related to their assigned state and lab type.</li>
                    <li><i className="bx bx-paper-plane" /> Admin can delete users, but cannot delete themselves.</li>
                </ul>
            </div>

            {/* CREATE USER POPUP */}
            {isPopupOpen && (
                <div className="popup-overlay">
                    <div className="add-user-popup">
                        <h2>Register New User</h2>
                        <form>
                            <div><input type="text"  name="fname"  placeholder="First Name" style={inp} value={newUser.fname}    onChange={handleInputChange} required /></div>
                            <div><input type="text"  name="lname"  placeholder="Last Name"  style={inp} value={newUser.lname}    onChange={handleInputChange} required /></div>
                            <div><input type="email" name="email"  placeholder="Email"      style={inp} value={newUser.email}    onChange={handleInputChange} required /></div>
                            <div><input type="text"  name="phone"  placeholder="Phone" maxLength={10} style={inp} value={newUser.phone} onChange={handleInputChange} required /></div>
                            <div>
                                <select name="userType" style={inp} value={newUser.userType} onChange={handleInputChange} required>
                                    <option value="">Select User Type</option>
                                    <option value="Admin">Admin</option>
                                    <option value="Mentor">Mentor</option>
                                    <option value="State Officer">State Officer</option>
                                </select>
                            </div>
                            {newUser.userType !== 'Admin' && (
                                <>
                                    <div>
                                        <select name="state" style={inp} value={newUser.state} onChange={handleInputChange} required>
                                            <option value="">Select State</option>
                                            {states.map((s,i) => <option key={i} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <select name="assignedLab" style={inp} value={newUser.assignedLab} onChange={handleInputChange}>
                                            <option value="">Select Lab Type to Assign</option>
                                            {availableLabs.length > 0
                                                ? availableLabs.map(lab => (
                                                    <option key={lab.lab_type_id} value={lab.lab_type_id}>
                                                        {lab.lab_type_name}
                                                    </option>
                                                ))
                                                : <option disabled>No labs available</option>
                                            }
                                        </select>
                                        <small style={{ color:'#666', fontSize:12 }}>{availableLabs.length} lab type(s) available</small>
                                    </div>
                                </>
                            )}
                            <div className="popup-actions">
                                <button type="submit" className="save-btn" disabled={loading}
                                    onClick={e => handleCreateUser(e, newUser, setNewUser, setLoading, setIsPopupOpen)}>
                                    {loading ? 'Saving…' : 'Save'}
                                </button>
                                <button type="button" className="cancel-btn" onClick={() => setIsPopupOpen(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* EDIT USER POPUP */}
            {editPopupOpen && selectedUser && (
                <div className="popup-overlay">
                    <div className="add-user-popup">
                        <h2>Edit User Details</h2>
                        <form>
                            <div>
                                <label style={{ fontSize:13, color:'#666', display:'block', marginBottom:8 }}>
                                    📧 {selectedUser.email}
                                </label>
                            </div>
                            <div><input type="text" name="fname" placeholder="First Name" style={inp} value={selectedUser.fname} onChange={handleUpdateChange} required /></div>
                            <div><input type="text" name="lname" placeholder="Last Name"  style={inp} value={selectedUser.lname} onChange={handleUpdateChange} required /></div>
                            <div><input type="text" name="phone" placeholder="Phone" maxLength={10} style={inp} value={selectedUser.phone||''} onChange={handleUpdateChange} required /></div>
                            {selectedUser.role_id !== 1 && (
                                <>
                                    <div>
                                        <select name="state" style={inp} value={selectedUser.state||''} onChange={handleUpdateChange} required>
                                            <option value="">Select State</option>
                                            {states.map((s,i) => <option key={i} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <select name="assignedLab" style={inp} value={selectedUser.assignedLab||''} onChange={handleUpdateChange}>
                                            <option value="">Select Lab Type to Assign</option>
                                            {availableLabs.length > 0
                                                ? availableLabs.map(lab => (
                                                    <option key={lab.lab_type_id} value={lab.lab_type_id}>
                                                        {lab.lab_type_name}
                                                    </option>
                                                ))
                                                : <option disabled>No lab types available</option>
                                            }
                                        </select>
                                        <small style={{ color:'#666', fontSize:12 }}>
                                            {availableLabs.length} lab type(s) available
                                            {selectedUser.assignedLab && (
                                                <span style={{ marginLeft:8, color:'#2e7d32', fontWeight:600 }}>
                                                    ✓ Currently: {
                                                        availableLabs.find(l => String(l.lab_type_id) === String(selectedUser.assignedLab))?.lab_type_name || '—'
                                                    }
                                                </span>
                                            )}
                                        </small>
                                    </div>
                                </>
                            )}
                            <div className="popup-actions">
                                <button type="submit" className="save-btn" disabled={loading}
                                    onClick={e => handleUpdateUserDetails(e, selectedUser, setLoading, setEditPopupOpen)}>
                                    {loading ? 'Updating…' : 'Update'}
                                </button>
                                <button type="button" className="cancel-btn" onClick={() => setEditPopupOpen(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserAccess;